import type { AccessRuleInputData } from '@sunstone/api-client/hooks';
import { useListAccessibleDeployments } from '@sunstone/api-client/hooks';
import { Checkbox, Input, Label } from '@sunstone/ui';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuthToken } from '@/features/auth';
import { useDebounce } from '@/hooks/useDebounce';

export interface DeploymentItem {
  id: string;
  displayName: string;
}

interface DeploymentPickerProps {
  tenantId: string;
  /** Scopes to a partner's descendant tenants instead of tenantId — a
   * partner owns no deployments directly, only its child tenants do.
   * Mutually exclusive with tenantId (pass tenantId='' when set). */
  parentTenantId?: string;
  value: string[];
  onChange: (ids: string[]) => void;
  search: string;
  onSearchChange: (search: string) => void;
  /** The outer (role) and inner (user) access-rule envelopes to scope this
   * picker's rows to (accessrule.gateBoth) — server-side now, via
   * ListAccessibleDeployments. Undefined/empty means no candidate rules at
   * all, i.e. every deployment in scope. */
  roleRules?: AccessRuleInputData[];
  userRules?: AccessRuleInputData[];
}

/**
 * Flat, searchable, paginated deployment picker — the "Specific deployments"
 * mode of the Deployments section (SUN-408 round 2 hierarchy). Replaces the
 * deployment level of the old ResourceTree: deployments and workspaces are
 * now two independent sections rather than one nested tree, so there's no
 * more tri-state/implicit-child-selection concept to carry here.
 */
export function DeploymentPicker({
  tenantId,
  parentTenantId,
  value,
  onChange,
  search,
  onSearchChange,
  roleRules,
  userRules,
}: DeploymentPickerProps) {
  const { token } = useAuthToken();
  const debouncedSearch = useDebounce(search);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState<DeploymentItem[]>([]);
  const { data } = useListAccessibleDeployments(token, tenantId, parentTenantId, {
    search: debouncedSearch,
    pageSize: 100,
    page,
    roleRules,
    userRules,
  });
  const totalCount = data?.totalCount ?? 0;
  const selected = new Set(value);

  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedSearch is a trigger dep — the effect resets accumulation whenever it changes, without referencing its value in the body.
  useEffect(() => {
    setPage(1);
    setLoaded([]);
  }, [debouncedSearch]);

  useEffect(() => {
    const fetched = data?.deployments ?? [];
    if (fetched.length === 0) return;
    setLoaded((prev) => {
      const merged = [...prev];
      for (const dep of fetched) {
        if (!merged.some((m) => m.id === dep.id))
          merged.push({ id: dep.id, displayName: dep.displayName });
      }
      return merged;
    });
  }, [data]);

  function toggle(id: string) {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search deployments…"
          className="pl-8 h-8 text-sm"
        />
      </div>
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground px-1">{value.length} selected</p>
      )}
      {loaded.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          {debouncedSearch
            ? 'No matching deployments.'
            : 'No deployments are currently accessible.'}
        </p>
      ) : (
        <div className="rounded-md border max-h-64 overflow-y-auto divide-y">
          {loaded.map((dep) => (
            <div key={dep.id} className="flex items-center gap-2 py-1.5 px-3">
              <Checkbox
                id={`deployment-${dep.id}`}
                checked={selected.has(dep.id)}
                onCheckedChange={() => toggle(dep.id)}
              />
              <Label
                htmlFor={`deployment-${dep.id}`}
                className="text-sm cursor-pointer flex-1 truncate"
              >
                {dep.displayName}
              </Label>
            </div>
          ))}
        </div>
      )}
      {totalCount > loaded.length && (
        <button
          type="button"
          className="text-xs text-primary hover:underline px-1"
          onClick={() => setPage((p) => p + 1)}
        >
          Load more ({loaded.length} of {totalCount})
        </button>
      )}
    </div>
  );
}
